import * as React from "react";
import styled, { css } from "styled-components";
import { Theme } from "../../../theme";
import { useTheme } from "@material-ui/styles";
import { CameraHook } from "../../../hooks/useCameras";
import { CameraOptionsHook } from "../../../hooks/useCameraOptions";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import luxon from "@date-io/luxon";

import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import PauseCircleFilledIcon from "@material-ui/icons/PauseCircleFilled";
import Button from "@material-ui/core/Button";
import ViewHeadlineIcon from "@material-ui/icons/ViewHeadline";
import ViewListIcon from "@material-ui/icons/ViewList";
import LayersIcon from "@material-ui/icons/Layers";
import LayersClearIcon from "@material-ui/icons/LayersClear";
import {
  ButtonGroup,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
} from "@material-ui/core";

const StyledSection = styled.section`
  /* border: 1px solid black; */
  border-radius: 5px;
  /* 40px */
  height: auto;
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
  background-color: #fff;

  .controlsGroup {
    /* border: 1px solid red; */
    border-radius: 5px;
    display: flex;
    padding: 8px;
    flex: 1;
  }
  #cameraControls {
  }
  #playbackControls {
  }

  .riskBadge {
    color: #000;
    font-size: 14px;
    padding: 4px;
  }
  #highRisk {
    background: #ed1c2480;
  }
  #medRisk {
    background: #f2652280;
  }
  #lowRisk {
    background: #00a14b80;
  }
  #total {
    background: #fff;
  }
  .spacer {
    flex: 1;
  }

  .siteSelect {
    width: 130px;
    margin-top: 2px;
    padding-left: 16px;
    /* height: 40px; */
  }
  .siteSelectInner {
    padding: 0px;
    border-radius: 5px;
  }
  .liveButton {
    font-size: 14px;
  }
  .liveIcon {
    margin-left: 16px;
  }
  .datePicker {
    padding-top: 2px;
    width: 220px;
    padding-right: 8px;
  }
  .speedButton {
    color: #222;
    font-size: 14px;
    font-weight: 400;
    padding: 4px;
  }
  .button {
    color: #222;
    padding-left: 8px;
    padding-right: 8px;
  }

  ${({ theme }: { theme: Theme }) => {
    return css``;
  }};
`;

export interface ControlBarProps {
  cameraOptionsHook: CameraOptionsHook;
  sites: string[];
  risks: { low: number; medium: number; high: number };
}

const ControlBar: React.FC<ControlBarProps> = props => {
  const { risks, cameraOptionsHook } = props;
  const { options, setters } = cameraOptionsHook;
  const {
    siteFilter,
    startTime,
    speed,
    live,
    listViewOn,
    heatmapsOn,
  } = options;
  const {
    setHeatmapsOn,
    setListViewOn,
    setSiteFilter,
    setStartTime,
    setSpeed,
    setLive,
    setRiskFilter,
  } = setters;
  const theme: Theme = useTheme();
  const playSpeeds = [1, 2, 4, 8, 16];

  return (
    <>
      <StyledSection theme={theme}>
        <div className="controlsGroup" id="cameraControls">
          <ButtonGroup size="small" aria-label="small outlined button group">
            <Button
              className="riskBadge"
              id="highRisk"
              onClick={() => setRiskFilter(4)}
            >
              {risks.high}
            </Button>
            <Button
              className="riskBadge"
              id="medRisk"
              onClick={() => setRiskFilter(3)}
            >
              {risks.medium}
            </Button>
            <Button
              className="riskBadge"
              id="lowRisk"
              onClick={() => setRiskFilter(1)}
            >
              {risks.low}
            </Button>
            <Button
              className="riskBadge"
              id="total"
              onClick={() => setRiskFilter(0)}
            >
              {risks.high + risks.medium + risks.low}
            </Button>
          </ButtonGroup>

          <div className="spacer"></div>
          <ButtonGroup
            size="small"
            aria-label="small outlined button group"
            disabled={!listViewOn} //Disable if in column view
          >
            {playSpeeds.map(playSpeed => (
              <Button
                className="speedButton"
                onClick={() => setSpeed(playSpeed)}
                color={speed === playSpeed ? "primary" : "secondary"}
              >
                {`${playSpeed}x`}
              </Button>
            ))}
          </ButtonGroup>
          <div className="spacer"></div>
          <Button
            variant="outlined"
            className="liveButton"
            onClick={() => setLive(!live)}
            disabled={!listViewOn} //Disable if in column view
          >
            Live
            {live ? (
              <PauseCircleFilledIcon className="liveIcon" />
            ) : (
              <PlayCircleFilledIcon className="liveIcon" />
            )}
          </Button>
        </div>
        <div className="controlsGroup" id="playbackControls">
          <MuiPickersUtilsProvider utils={luxon}>
            <KeyboardDateTimePicker
              disabled={!listViewOn} //Disable if in column view
              disableFuture
              className="datePicker"
              variant="inline"
              //   ampm={false}
              //   label="Select Time"
              value={startTime}
              onChange={date => {
                if (!!date) setStartTime(date.toJSDate());
              }}
              onError={console.log}
              format="yyyy/MM/dd hh:mm a"
            />
          </MuiPickersUtilsProvider>
          <FormControl className="siteSelect" variant="outlined">
            <Select
              value={siteFilter}
              onChange={(e: React.ChangeEvent<any>) => {
                setSiteFilter(e.target.value);
              }}
              name="age"
              displayEmpty
              className="siteSelectInner"
            >
              <MenuItem value="">All sites</MenuItem>
              {props.sites.map(site => (
                <MenuItem value={site}>{site}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="spacer"></div>
          <IconButton
            className="button"
            size="small"
            aria-label="heatmap-toggle"
            onClick={() => setHeatmapsOn(!heatmapsOn)}
            disabled={!listViewOn} //Disable if in column view
          >
            {heatmapsOn ? <LayersIcon /> : <LayersClearIcon />}
          </IconButton>
          <IconButton
            className="button"
            size="small"
            aria-label="view-toggle"
            onClick={() => setListViewOn(!listViewOn)}
          >
            {listViewOn ? <ViewHeadlineIcon /> : <ViewListIcon />}
          </IconButton>
        </div>
      </StyledSection>
    </>
  );
};

export default ControlBar;
